import React from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@codes/ui-kit/components/ui/dialog';
import { Calendar, Clock, FileText } from 'lucide-react';

import { MessageBubble } from './MessageBubble';
import { DOCUMENT_CONSTANTS } from '../constants/document';
import { formatDocumentDate } from '../utils/document';

import type { DocumentModalProps } from '../types/document';

export const DocumentModal: React.FC<DocumentModalProps> = ({
    isOpen,
    onOpenChange,
    documentData,
    isLoading = false,
}) => {
    const { t } = useTranslation();

    const renderDocumentContent = () => {
        if (isLoading || !documentData) {
            return (
                <div className="flex items-center gap-3 justify-center py-12">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    <span className="text-sm text-muted-foreground">{t('agent.document.loading')}</span>
                </div>
            );
        }

        return (
            <>
                {/* Document metadata */}
                <div className="space-y-2 mb-3">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            <span className="font-mono bg-muted/50 rounded px-1.5 py-0.5">{documentData.id}</span>
                        </div>
                        {documentData.externalType && (
                            <div className="flex items-center gap-1">
                                <span className="capitalize bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded">
                                    {documentData.externalType}
                                </span>
                            </div>
                        )}
                    </div>

                    {(documentData.createdAt || documentData.updatedAt || documentData.meta$?.date) && (
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            {(documentData.createdAt || documentData.meta$?.date) && (
                                <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    <span>
                                        {documentData.meta$?.date || formatDocumentDate(documentData.createdAt)}
                                    </span>
                                </div>
                            )}
                            {documentData.updatedAt && documentData.updatedAt !== documentData.createdAt && (
                                <div className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    <span>
                                        {t('agent.document.modified')} {formatDocumentDate(documentData.updatedAt)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Document body */}
                <div className="space-y-4">
                    <div className="flex justify-start" role="group" aria-label="Document content">
                        <div className="relative group w-full text-title py-[10px] rounded-xl">
                            <div className="flex gap-3">
                                <div className="flex-1 min-w-0">
                                    <MessageBubble
                                        isUser={false}
                                        messageId={`doc-${documentData.id}`}
                                        content={documentData.content}
                                        copyButtonContent={documentData.content}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const dialogTitle = documentData?.title || t('ai.chatbot.reference_documents', '참조 문서');

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent
                className={`max-w-4xl min-w-[800px] max-h-[80vh] overflow-y-auto ${DOCUMENT_CONSTANTS.MODAL_Z_INDEX}`}
            >
                <DialogHeader>
                    <DialogTitle>{dialogTitle}</DialogTitle>
                </DialogHeader>
                {renderDocumentContent()}
            </DialogContent>
        </Dialog>
    );
};
